import { z } from 'zod';

import { imageUrlSchema, slugSchema } from './store';

/**
 * Passatempos (RF-A5), entre a site-api, o site e o
 * back-office. Datas em ISO-8601 (UTC); o site e o back-office mostram-nas em
 * hora de Lisboa.
 *
 * Prefixos HTTP:
 *   - `/api/giveaways/...`       site publico (paginas e participacoes);
 *   - `/api/site/giveaways/...`  back-office (`giveaways.manage`), so em gestao.
 *
 * Cada passatempo escolhe o publico (`audience`): so associados, membros e
 * associados, ou todas as contas. O nivel conta no momento em que se entra: a
 * propria conta, ou um jovem inscrito pelo encarregado com a autorizacao ativa
 * (RN-A9).
 * Participar da 1 entrada; as acoes do passatempo (seguir nas redes, codigos
 * secretos) dao entradas extra. O sorteio e feito no back-office depois de
 * fechar, com um gerador aleatorio criptografico, ponderado pelas entradas; os
 * vencedores so se veem no back-office e pelo proprio.
 */

/**
 * `draft` so no back-office; `published` aberto ou a espera do sorteio;
 * `drawn` com vencedores; `cancelled` ja nao se sorteia.
 */
export const giveawayStatusSchema = z.enum(['draft', 'published', 'drawn', 'cancelled']);
export type GiveawayStatus = z.infer<typeof giveawayStatusSchema>;

/** `not_yet` abre em `opensAt`; `closed` fechou (ou foi sorteado ou cancelado). */
export const giveawayWindowSchema = z.enum(['not_yet', 'open', 'closed']);
export type GiveawayWindow = z.infer<typeof giveawayWindowSchema>;

/**
 * Quem pode participar: `associates` so associados (quota em vigor);
 * `members` membros e associados; `everyone` todas as contas.
 */
export const giveawayAudienceSchema = z.enum(['associates', 'members', 'everyone']);
export type GiveawayAudience = z.infer<typeof giveawayAudienceSchema>;

export const MAX_GIVEAWAY_RULES = 20_000;

const datetimeSchema = z.string().datetime({ offset: true });

// ---------------------------------------------------------------------------
// Acoes (entradas extra)
// ---------------------------------------------------------------------------

/**
 * Seguir no Twitch, Instagram, YouTube ou X: por confianca, conta quando se
 * carrega no botao. `secret_code`: um codigo dito num direto ou num video.
 */
export const giveawayActionKindSchema = z.enum([
  'follow_twitch',
  'follow_instagram',
  'follow_youtube',
  'follow_x',
  'secret_code',
]);
export type GiveawayActionKind = z.infer<typeof giveawayActionKindSchema>;

export const MAX_GIVEAWAY_ACTIONS = 20;
export const MAX_REFERRAL_LIMIT = 100;
export const MAX_ACTION_ENTRIES = 20;

/** Codigos sem espacos, comparados em maiusculas. */
export const normalizeGiveawayCode = (code: string) => code.trim().toUpperCase();

const giveawayCodeSchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z0-9-]{4,40}$/, 'de 4 a 40 letras, numeros ou hifens')
  .transform(normalizeGiveawayCode);

export const giveawayActionInputSchema = z
  .object({
    /** `null` = acao nova. */
    id: z.string().uuid().nullable().default(null),
    kind: giveawayActionKindSchema,
    /** "@growupesports" ou "Codigo do direto de sexta". */
    label: z.string().trim().max(120).default(''),
    /** A pagina a seguir (so nas acoes de seguir). */
    url: z
      .string()
      .trim()
      .max(500)
      .regex(/^https:\/\/\S+$/, 'use um link https://')
      .nullable()
      .default(null),
    entries: z.number().int().min(1).max(MAX_ACTION_ENTRIES).default(1),
    /** So nos codigos secretos. */
    code: giveawayCodeSchema.nullable().default(null),
    validFrom: datetimeSchema.nullable().default(null),
    validUntil: datetimeSchema.nullable().default(null),
  })
  .superRefine((a, ctx) => {
    if (a.kind === 'secret_code') {
      if (a.code === null)
        ctx.addIssue({ code: 'custom', path: ['code'], message: 'indique o codigo' });
    } else if (a.url === null) {
      ctx.addIssue({ code: 'custom', path: ['url'], message: 'indique o link da conta' });
    }
    if (a.validFrom && a.validUntil && new Date(a.validUntil) <= new Date(a.validFrom)) {
      ctx.addIssue({
        code: 'custom',
        path: ['validUntil'],
        message: 'tem de acabar depois de comecar',
      });
    }
  });
export type GiveawayActionInput = z.infer<typeof giveawayActionInputSchema>;

/** Uma acao como o site a ve: sem o codigo. */
export const publicGiveawayActionSchema = z.object({
  id: z.string().uuid(),
  kind: giveawayActionKindSchema,
  label: z.string(),
  url: z.string().nullable(),
  entries: z.number().int(),
  validFrom: z.string().nullable(),
  validUntil: z.string().nullable(),
});
export type PublicGiveawayAction = z.infer<typeof publicGiveawayActionSchema>;

export const adminGiveawayActionSchema = publicGiveawayActionSchema.extend({
  code: z.string().nullable(),
  /** Quantas pessoas ja a fizeram. */
  done: z.number().int(),
});
export type AdminGiveawayAction = z.infer<typeof adminGiveawayActionSchema>;

export const MAX_GIVEAWAY_WINNERS = 50;

// ---------------------------------------------------------------------------
// Site publico
// ---------------------------------------------------------------------------

export const publicGiveawaySchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  description: z.string(),
  prize: z.string(),
  imageUrl: z.string().nullable(),
  winnersCount: z.number().int(),
  audience: giveawayAudienceSchema,
  /** Regulamento em texto simples ('' = sem regulamento proprio). */
  rules: z.string(),
  actions: z.array(publicGiveawayActionSchema),
  /** Entradas por amigo convidado (`null` = sem convites), ate `referralLimit` amigos. */
  referralEntries: z.number().int().nullable(),
  referralLimit: z.number().int(),
  opensAt: z.string(),
  closesAt: z.string(),
  status: z.enum(['published', 'drawn']),
  window: giveawayWindowSchema,
  drawnAt: z.string().nullable(),
});
export type PublicGiveaway = z.infer<typeof publicGiveawaySchema>;

/** Porque uma conta (ou um jovem) nao pode participar agora. */
export const giveawayBlockSchema = z.enum([
  /** So associados (quota em vigor). */
  'associates_only',
  /** So membros e associados. */
  'members_only',
  /** O jovem sem a autorizacao de encarregado ativa. */
  'no_guardian_authorization',
  'not_open_yet',
  'closed',
]);
export type GiveawayBlock = z.infer<typeof giveawayBlockSchema>;

/** Uma pessoa que pode participar: a propria conta ou um jovem do encarregado. */
export const giveawayParticipantSchema = z.object({
  accountId: z.string().uuid(),
  login: z.string(),
  /** A propria conta (`false` = um jovem de quem e encarregado). */
  self: z.boolean(),
  entered: z.boolean(),
  /** `null` antes do sorteio ou sem participacao. */
  won: z.boolean().nullable(),
  /** `null` = pode participar (ou sair) agora. */
  blocked: giveawayBlockSchema.nullable(),
  /** Entradas no sorteio: 1 por participar + as das acoes feitas (0 sem participar). */
  entries: z.number().int(),
  /** As acoes ja feitas (ids). */
  actionsDone: z.array(z.string().uuid()),
  /** O codigo do link pessoal para convidar amigos (`null` sem participar). */
  inviteCode: z.string().nullable(),
  /** Amigos que ja contaram (no maximo `referralLimit`). */
  invites: z.number().int(),
});
export type GiveawayParticipant = z.infer<typeof giveawayParticipantSchema>;

/** `GET /api/giveaways/:slug/me`: eu e os meus jovens neste passatempo. */
export const giveawayMeSchema = z.object({
  participants: z.array(giveawayParticipantSchema),
});
export type GiveawayMe = z.infer<typeof giveawayMeSchema>;

/** `POST /api/giveaways/:slug/entries` e `.../withdraw`. Sem `youthAccountId` = a propria conta. */
export const giveawayEntryRequestSchema = z.object({
  youthAccountId: z.string().uuid().nullable().default(null),
});
export type GiveawayEntryRequest = z.infer<typeof giveawayEntryRequestSchema>;

/** `POST /api/giveaways/:slug/actions/:actionId`: marcar uma acao de seguir como feita. */
export const giveawayActionRequestSchema = giveawayEntryRequestSchema;

/** `POST /api/giveaways/:slug/codes`: usar um codigo secreto. */
export const giveawayCodeRequestSchema = z.object({
  youthAccountId: z.string().uuid().nullable().default(null),
  code: z.string().trim().min(1).max(60).transform(normalizeGiveawayCode),
});
export type GiveawayCodeRequest = z.infer<typeof giveawayCodeRequestSchema>;

/**
 * `POST /api/giveaways/:slug/invites`: quem chegou pelo link de um amigo.
 * Conta se a conta foi criada depois de o passatempo abrir e tem o email
 * confirmado; uma vez por passatempo.
 */
export const giveawayInviteRequestSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^[a-z0-9]{6,20}$/),
});
export type GiveawayInviteRequest = z.infer<typeof giveawayInviteRequestSchema>;

export const giveawayInviteResultSchema = z.object({
  /** Quem convidou (login; de um jovem, so o login). */
  inviterLogin: z.string(),
  /** `false` = quem convidou ja chegou ao limite: fica registado mas nao da entradas. */
  counted: z.boolean(),
});
export type GiveawayInviteResult = z.infer<typeof giveawayInviteResultSchema>;

// ---------------------------------------------------------------------------
// Back-office
// ---------------------------------------------------------------------------

export const giveawayInputSchema = z
  .object({
    slug: slugSchema,
    title: z.string().trim().min(1).max(120),
    summary: z.string().trim().max(300).default(''),
    description: z.string().trim().max(20_000).default(''),
    /** O que se ganha, numa frase: "2 camisolas oficiais autografadas". */
    prize: z.string().trim().min(1).max(200),
    imageUrl: imageUrlSchema,
    winnersCount: z.number().int().min(1).max(MAX_GIVEAWAY_WINNERS).default(1),
    audience: giveawayAudienceSchema.default('associates'),
    /** Regulamento em texto simples, mostrado na pagina do passatempo. */
    rules: z.string().trim().max(MAX_GIVEAWAY_RULES).default(''),
    actions: z.array(giveawayActionInputSchema).max(MAX_GIVEAWAY_ACTIONS).default([]),
    /** Entradas por amigo convidado; `null` = sem convites. */
    referralEntries: z.number().int().min(1).max(MAX_ACTION_ENTRIES).nullable().default(null),
    referralLimit: z.number().int().min(1).max(MAX_REFERRAL_LIMIT).default(10),
    /** `null` = abre ao publicar. */
    opensAt: datetimeSchema.nullable().default(null),
    closesAt: datetimeSchema,
  })
  .refine((g) => g.opensAt === null || new Date(g.closesAt) > new Date(g.opensAt), {
    message: 'tem de fechar depois de abrir',
    path: ['closesAt'],
  })
  .refine(
    (g) => {
      const codes = g.actions.flatMap((a) => (a.code === null ? [] : [a.code]));
      return new Set(codes).size === codes.length;
    },
    { message: 'ha dois codigos iguais', path: ['actions'] },
  );
export type GiveawayInput = z.infer<typeof giveawayInputSchema>;

export const giveawayWinnerSchema = z.object({
  position: z.number().int(),
  accountId: z.string().uuid(),
  /** So o login (RN-A16), tambem para jovens. */
  login: z.string(),
  /** Quem recebe o aviso: a propria conta, ou o encarregado do jovem. */
  contactLogin: z.string(),
  drawnAt: z.string(),
  /** Desistiu ou nao respondeu: foi sorteado outro no lugar. */
  forfeited: z.boolean(),
  note: z.string().nullable(),
});
export type GiveawayWinner = z.infer<typeof giveawayWinnerSchema>;

export const adminGiveawaySchema = z.object({
  id: z.string().uuid(),
  slug: z.string(),
  title: z.string(),
  summary: z.string(),
  description: z.string(),
  prize: z.string(),
  imageUrl: z.string().nullable(),
  winnersCount: z.number().int(),
  audience: giveawayAudienceSchema,
  rules: z.string(),
  actions: z.array(adminGiveawayActionSchema),
  referralEntries: z.number().int().nullable(),
  referralLimit: z.number().int(),
  opensAt: z.string().nullable(),
  closesAt: z.string(),
  status: giveawayStatusSchema,
  window: giveawayWindowSchema,
  publishedAt: z.string().nullable(),
  drawnAt: z.string().nullable(),
  cancelledAt: z.string().nullable(),
  cancelReason: z.string().nullable(),
  entries: z.number().int(),
  winners: z.array(giveawayWinnerSchema),
  createdAt: z.string(),
});
export type AdminGiveaway = z.infer<typeof adminGiveawaySchema>;

export const adminGiveawayEntrySchema = z.object({
  id: z.string().uuid(),
  accountId: z.string().uuid(),
  login: z.string(),
  /** Quem a fez: a propria conta, ou o encarregado do jovem. */
  enteredByLogin: z.string(),
  /** Entradas no sorteio (1 + acoes + convites). */
  entries: z.number().int(),
  /** Os amigos que convidou (logins), pela ordem; os que passam o limite tambem. */
  invited: z.array(z.string()),
  createdAt: z.string(),
});
export type AdminGiveawayEntry = z.infer<typeof adminGiveawayEntrySchema>;

export const cancelGiveawayRequestSchema = z.object({
  reason: z.string().trim().min(1).max(300),
});

export const forfeitWinnerRequestSchema = z.object({
  note: z.string().trim().max(300).nullable().default(null),
});
